// Hooks de leitura (React Query) do domínio notification-center.
// O acesso HTTP fica na camada de serviço (src/services).
import { ENDPOINTS } from "@/constants"
import { useQuery } from "@/libs"
import type { NotificationCenterTemplatesFilters } from "@/services/notification-center"
import {
  getNotificationCenterDashboardOverview,
  getNotificationCenterNotificationDetail,
  getNotificationCenterNotifications,
  getNotificationCenterTemplateEvents,
  getNotificationCenterTemplateVersions,
  getNotificationCenterWhiteLabelConfigurations,
  getNotificationCenterWhiteLabelEvents,
  getNotificationCenterWhiteLabelProviders,
  getNotificationCenterWhiteLabels,
  getNotificationCenterWhiteLabelTemplates,
} from "@/services/notification-center"
import type {
  NotificationCenterOverviewParamsType,
  NotificationSearchFiltersType,
} from "@/types/notification-center"

// Overview do Dashboard (§4.1) — período/WL na query key garantem
// refetch quando o filtro muda.
export const useNotificationCenterDashboardOverview = (
  params: NotificationCenterOverviewParamsType = {}
) => {
  return useQuery({
    queryKey: [
      ENDPOINTS.NOTIFICATION_CENTER_DASHBOARD_OVERVIEW,
      params,
    ],
    queryFn: () =>
      getNotificationCenterDashboardOverview(params),
  })
}

// Busca paginada de envios (Consulta) — filtros na query key
// garantem refetch quando a URL muda.
export const useNotificationCenterNotifications = (
  filters: NotificationSearchFiltersType = {}
) => {
  return useQuery({
    queryKey: [
      ENDPOINTS.NOTIFICATION_CENTER_NOTIFICATIONS,
      filters,
    ],
    queryFn: () =>
      getNotificationCenterNotifications(filters),
  })
}

// Query key do detalhe do envio — compartilhada com a mutation
// otimista de reenvio (notification-mutations.ts).
export const getNotificationDetailQueryKey = (
  uuid?: string
) => [
  ENDPOINTS.NOTIFICATION_CENTER_NOTIFICATION_DETAIL,
  { uuid },
]

// Detalhe de um envio (scheduler) — recebe o schedulerId (SCHED-…) e
// devolve o payload ÚNICO com todos os canais em channels[]. Só busca
// com schedulerId presente.
// placeholderData: undefined ANULA o default global
// (keepPreviousData em src/providers/index.tsx): sem isso, ao navegar
// entre envios (schedulerId diferente) o detalhe manteria o dado do
// envio anterior visível (isLoading=false) e o SKELETON não apareceria.
// Assim cada troca de envio mostra o skeleton até o novo carregar.
export const useNotificationCenterNotificationDetail = (
  schedulerId?: string
) => {
  return useQuery({
    queryKey: getNotificationDetailQueryKey(schedulerId),
    queryFn: () =>
      getNotificationCenterNotificationDetail(
        schedulerId ?? ""
      ),
    enabled: Boolean(schedulerId),
    placeholderData: undefined,
  })
}

export const useNotificationCenterWhiteLabels = () => {
  return useQuery({
    queryKey: [ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS],
    queryFn: getNotificationCenterWhiteLabels,
  })
}

export const useNotificationCenterWhiteLabelsConfigurations =
  (whiteLabelId: string) => {
    return useQuery({
      queryKey: [
        ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_CONFIGURATIONS,
        { whiteLabelId },
      ],
      queryFn: () =>
        getNotificationCenterWhiteLabelConfigurations(
          whiteLabelId
        ),
      enabled: Boolean(whiteLabelId),
    })
  }

export const useNotificationCenterWhiteLabelsTemplates = ({
  whiteLabelId,
  enabled,
  active,
  channel,
}: {
  whiteLabelId: string
} & NotificationCenterTemplatesFilters) => {
  return useQuery({
    queryKey: [
      ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_TEMPLATES,
      { whiteLabelId, enabled, active, channel },
    ],
    queryFn: () =>
      getNotificationCenterWhiteLabelTemplates(
        whiteLabelId,
        { enabled, active, channel }
      ),
    enabled: Boolean(whiteLabelId),
  })
}

// Query key das versões do template (§4.5) — compartilhada com as
// mutations otimistas do editor (template-editor-mutations.ts).
export const getTemplateVersionsQueryKey = (
  templateUuid: string
) => [
  ENDPOINTS.NOTIFICATION_CENTER_TEMPLATE_VERSIONS,
  { templateUuid },
]

// Versões do template (§4.4/§4.5): variables/usedBy + lifecycle.
// Também usada como ENRIQUECIMENTO por linha na lista de Templates
// (canal, nº de versões e "plugado em" não vêm da lista) — o
// staleTime alto evita refetch por célula.
export const useNotificationCenterTemplateVersions = (
  templateUuid?: string,
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: getTemplateVersionsQueryKey(
      templateUuid ?? ""
    ),
    queryFn: () =>
      getNotificationCenterTemplateVersions(
        templateUuid ?? ""
      ),
    enabled:
      Boolean(templateUuid) && (options.enabled ?? true),
    staleTime: 60_000,
  })
}

// Query key dos eventos do template (test-send por cenário) —
// separada das versões porque tem cardinalidade e cache próprios.
export const getTemplateEventsQueryKey = (
  templateUuid: string
) => [
  ENDPOINTS.NOTIFICATION_CENTER_TEMPLATE_EVENTS,
  { templateUuid },
]

// Eventos do template (test-send por cenário): TODOS os eventos com a
// flag `associated`. Só busca com o dialog aberto (enabled) — o front
// pré-marca o(s) associado(s) no seletor de cenário.
export const useNotificationCenterTemplateEvents = (
  templateUuid?: string,
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: getTemplateEventsQueryKey(templateUuid ?? ""),
    queryFn: () =>
      getNotificationCenterTemplateEvents(
        templateUuid ?? ""
      ),
    enabled:
      Boolean(templateUuid) && (options.enabled ?? true),
    staleTime: 60_000,
  })
}

// Query key da configuração rica do WL (§4.3) — compartilhada com
// as mutations otimistas (configuration-mutations.ts).
export const getWhiteLabelEventsQueryKey = (
  whiteLabelId: string
) => [
  ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_EVENTS,
  { whiteLabelId },
]

// Configuração do WL (§4.3): canais/providers + eventos com
// templates por canal e agendamentos.
export const useNotificationCenterWhiteLabelEvents = (
  whiteLabelId: string
) => {
  return useQuery({
    queryKey: getWhiteLabelEventsQueryKey(whiteLabelId),
    queryFn: () =>
      getNotificationCenterWhiteLabelEvents(whiteLabelId),
    enabled: Boolean(whiteLabelId),
  })
}

// Query key dos Provedores do WL — compartilhada com as mutations
// otimistas (provider-mutations.ts).
export const getWhiteLabelProvidersQueryKey = (
  whiteLabelId: string
) => [
  ENDPOINTS.NOTIFICATION_CENTER_WHITE_LABELS_ID_PROVIDER,
  { whiteLabelId },
]

// Provedores do WL: associação canal→provider + catálogo global +
// config Zenvia (credenciais write-only e journeys).
export const useNotificationCenterWhiteLabelProviders = (
  whiteLabelId: string
) => {
  return useQuery({
    queryKey: getWhiteLabelProvidersQueryKey(whiteLabelId),
    queryFn: () =>
      getNotificationCenterWhiteLabelProviders(
        whiteLabelId
      ),
    enabled: Boolean(whiteLabelId),
  })
}
